/**
 * @file PageVisibilityCard.tsx
 * @description Видимость и удаление страницы в «Настройках страницы»
 * (phase19.16 Р1-6а; uiux-builder-shell.md §2.2, §6): статус, скрыть /
 * показать, «Сделать главной» (SB29), «Удалить страницу» — окном кабинета с
 * последствиями. Главную скрыть и удалить нельзя.
 *
 * Р3а (SB128): скрыть, показать, удалить — право публикации контента, после
 * подтверждения (`usePageVisibility`); удаление мягкое — страницу можно
 * восстановить 12 месяцев в «Удалённых страницах». Р3в (SB128, SB131): окно
 * удаления — `DeletePageDialog` с выбором переадресации со старого адреса.
 * Статус — реестр `PAGE_STATUS_META`.
 *
 * SB122: карточка — группа `visibility` панели «Настройки страницы».
 */

import { Eye, EyeOff, House, Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "../../ui/button";
import { useNotify } from "../../ui/NotifyProvider";
import { SettingsGroup } from "../../ui/settings-groups";
import { StatusPill } from "../../ui/StatusPill";
import { useBuilder } from "../builder-context";
import { DeletePageDialog } from "../dialogs/DeletePageDialog";
import { usePageVisibility } from "../../../hooks/site-builder/usePageVisibility";
import type { SiteRedirectTargetInput } from "../../../lib/api-site-redirects";
import type { SitePageDetail } from "../../../lib/api-site-pages";
import { pagePublishErrorText } from "../../../lib/site-builder/page-publish-errors";
import { PAGE_STATUS_META, pageStatusOf, withFreshChanges } from "../../../lib/site-builder/page-status";
import { errorCode, seoErrorText } from "../../../lib/site-seo/seo-errors";

export function PageVisibilityCard({ page }: { page: SitePageDetail }) {
  const b = useBuilder();
  const notify = useNotify();
  const visibility = usePageVisibility();
  const [deleting, setDeleting] = useState(false);
  const [asking, setAsking] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const home = page.kind === "SYSTEM_HOME";
  // Карточка страницы отстаёт от правок — статус по свежей сводке черновика.
  const current = withFreshChanges(page, b.publishing.changes);
  const status = PAGE_STATUS_META[pageStatusOf(current)];
  const busy = visibility.busy || deleting;
  // SB109: на страницу ведёт юридическая ссылка — скрыть и удалить нельзя; говорим заранее (QA Р3а D9).
  const referenced = (page.references?.length ?? 0) > 0;
  const lockedText = referenced ? `На страницу ведёт ссылка ${page.references?.[0]?.label ?? "«Условия доставки и оплаты»"} — скрыть или удалить её нельзя.` : undefined;

  // Адрес на сайте: у страницы с живой версией — её адрес на сайте (Р3а liveSlug).
  const liveSlug = page.live ? (page.liveSlug ?? page.slug) : null;

  async function onDelete(redirect: SiteRedirectTargetInput | null) {
    setDeleting(true);
    setDeleteError(null);
    try {
      await b.pages.remove(redirect);
      setAsking(false);
      await b.select({ pageId: null });
      notify.success(redirect ? `Страница удалена, со старого адреса /${liveSlug ?? ""} — переадресация` : "Страница удалена — восстановить её можно в «Удалённых страницах»");
    } catch (err) {
      // Отказ цели переадресации — фразы переадресаций; прочее — фразы страниц (SB109 и др.).
      const redirectError = errorCode(err)?.startsWith("site_redirect") ?? false;
      setDeleteError(redirectError ? seoErrorText(err, "Не удалось создать переадресацию.") : pagePublishErrorText(err, "Не удалось удалить страницу."));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <SettingsGroup id="visibility" title="Видимость" summary={`${home ? "главная · " : ""}${status.label}`}>
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
      </div>
      {home ? (
        <p className="type-caption text-on-surface-variant">Главную страницу нельзя скрыть или удалить.</p>
      ) : b.canPublishPages ? (
        <div className="flex flex-wrap gap-2">
          {current.isPublished ? (
            <Button variant="outline" loading={visibility.busy} disabled={busy || referenced} title={lockedText} onClick={() => void visibility.hide()}>
              <EyeOff aria-hidden />
              Скрыть с сайта
            </Button>
          ) : (
            <Button variant="outline" loading={visibility.busy} disabled={busy} onClick={() => void visibility.show()}>
              <Eye aria-hidden />
              Показать на сайте
            </Button>
          )}
          <Button variant="outline" disabled={busy} onClick={() => b.openSetHome(page.id)}>
            <House aria-hidden />
            Сделать главной
          </Button>
          <Button variant="ghost" className="text-destructive" loading={deleting} disabled={busy || referenced} title={lockedText} onClick={() => {
              setDeleteError(null);
              setAsking(true);
            }}>
            <Trash2 aria-hidden />
            Удалить страницу
          </Button>
          {lockedText ? <p className="type-caption w-full text-on-surface-variant">{lockedText}</p> : null}
        </div>
      ) : (
        <p className="type-caption text-on-surface-variant">Показать, скрыть, удалить страницу или сделать её главной может сотрудник с правом публикации контента.</p>
      )}
      <DeletePageDialog
        open={asking}
        title={page.title}
        pageId={page.id}
        liveAddress={liveSlug ? `/${liveSlug}` : null}
        busy={deleting}
        error={deleteError}
        onCancel={() => {
          setAsking(false);
          setDeleteError(null);
        }}
        onConfirm={(redirect) => void onDelete(redirect)}
      />
    </SettingsGroup>
  );
}
